import React, {useCallback, useContext, useMemo, useState} from "react";
import {useMarketplaceShareInfo} from "../store/providers/MarketplaceShareInfoProvider";
import {isNumber, useCurrency, useI18N} from "../common/common";
import {CurrencyContext} from "../store/providers/CurrencyProvider";
import {Box, Divider, makeStyles, Typography, useTheme} from "@material-ui/core";
import {DIRECT_BUY_POINTS_PER_SHARE} from "../common/constant";
import Tags from "@hapinest/theme/icons/streamlinehq/img/streamline-regular/interface-essential/tags-bookmarks/tags.svg";
import LightBulbShine from "@hapinest/theme/icons/streamlinehq/img/streamline-regular/lamps-lights-fire/light-bubbles/light-bulb-shine.svg";
import { StreamlineIcon } from "@hapinest/theme";
import { useBreakpoints } from "./useBreakpoints";

const useStyles = makeStyles((theme) => ({
    streamlineIcon: {
        color: "inherit",
        fontSize: theme.spacing(2),
        marginRight: theme.spacing(.5)
    },
    fitContentWidth: {
        minWidth: "fit-content"
    },
    lighBulbShineIcon: {
        fontSize: theme.spacing(2),
        marginRight: theme.spacing(2)
    }
}));

export const useDirectSalesPrices = (initialShares = 1) => {
    const classes = useStyles();
    const {getDirectSalesPricingData, directSalesDataLoading, directSalesDataError} = useMarketplaceShareInfo();
    const {formatCurrency} = useCurrency();
    const {i18n} = useI18N();
    const {currency} = useContext(CurrencyContext)?.[0];
    const theme = useTheme();
    const {isExtraSmallScreen} = useBreakpoints();

    const [directSalesData, setDirectSalesData] = useState();
    const [directSalesPricingDataError, setDirectSalesPricingDataError] = useState();
    const [shares, setShares] = useState(initialShares);
    const [total, setTotal] = useState();
    const [originalTotal, setOriginalTotal] = useState();
    const [pricesLoading, setPricesLoading] = useState(false);

    const loading = useMemo(() => {
        return pricesLoading || directSalesDataLoading;
    }, [pricesLoading, directSalesDataLoading]);

    const error = useMemo(() => {
        return directSalesDataError || directSalesPricingDataError;
    }, [directSalesDataError, directSalesPricingDataError]);

    const priceElements = useMemo(() => {
        const {
            isSharesPromotionActive,
            isAnnualChargePromotionActive,
            shareDiscountPercentage,
            name,
            annualChargeDiscountPercentage
        } = directSalesData?.promotionInfo || {};

        const share = directSalesData?.prices?.share;
        const sharesCharge = {
            original: share?.originalPrice,
            promo: share?.originalPromoPrice
        };

        const annualChargeData = directSalesData?.prices?.annualCharge;
        const annualCharge = {
            original: annualChargeData?.originalPrice,
            promo: annualChargeData?.originalPromoPrice,
        };

        const elements = [
            {
                id: "shares",
                title: shares + "x " + i18n("view.buy_a_share.directly_select_shares_dialog.selection_preview.shares_description"),
                description: i18n("view.buy_a_share.directly_select_shares_dialog.selection_preview.shares_note", {pointsPerShare: DIRECT_BUY_POINTS_PER_SHARE}),
                price: sharesCharge?.original,
                discountPrice: isSharesPromotionActive ? sharesCharge?.promo : undefined,
                discounts: (!loading && sharesCharge && isNumber(sharesCharge.original) && isNumber(sharesCharge.promo)) ? (
                    <>
                        {isSharesPromotionActive === true &&
                            <Box
                                mt={0.5}
                                color={theme.palette.primary[900]}
                                display="flex"
                                justifyContent="space-between"
                                gridGap={isExtraSmallScreen() ? 16 : 0}
                            >
                                <Box
                                    display="flex"
                                    alignItems="center"
                                >
                                    <StreamlineIcon className={classes.streamlineIcon}>
                                        <Tags/>
                                    </StreamlineIcon>

                                    <Typography
                                        variant="body1"
                                        color="inherit"
                                    >
                                        {`${shareDiscountPercentage}% ${name}`}
                                    </Typography>
                                </Box>

                                <Typography
                                    variant="body2"
                                    color="inherit"
                                    className={classes.fitContentWidth}
                                >
                                    {`-${formatCurrency(currency, sharesCharge.original - sharesCharge.promo)}`}
                                </Typography>
                            </Box>
                        }
                    </>
                ) : undefined
            },
            {
                id: "annualCharge",
                title: shares + "x " + i18n("view.buy_a_share.directly_select_shares_dialog.selection_preview.annual_charge_description"),
                description: i18n("view.buy_a_share.directly_select_shares_dialog.selection_preview.annual_charge_note"),
                price: annualCharge?.original,
                discountPrice: isAnnualChargePromotionActive ? annualCharge?.promo : undefined,
                discounts: (!loading && annualCharge && isNumber(annualCharge.original) && isNumber(annualCharge.promo)) ? (
                    <>
                        {isAnnualChargePromotionActive === true &&
                            <Box
                                mt={0.5}
                                color={theme.palette.primary[900]}
                                display="flex"
                                justifyContent="space-between"
                                gridGap={isExtraSmallScreen() ? 16 : 0}
                            >
                                <Box
                                    display="flex"
                                    alignItems="center"
                                >
                                    <StreamlineIcon className={classes.streamlineIcon}>
                                        <Tags/>
                                    </StreamlineIcon>

                                    <Typography
                                        variant="body1"
                                        color={"inherit"}
                                    >
                                        {`${annualChargeDiscountPercentage}% ${i18n("view.buy_a_share.directly_select_shares_dialog.selection_preview.annual_charge_promotion")}`}
                                    </Typography>
                                </Box>
                                <Typography
                                    variant="body2"
                                    color={"inherit"}
                                    className={classes.fitContentWidth}
                                >
                                    {`-${formatCurrency(currency, annualCharge.original - annualCharge.promo)}`}
                                </Typography>
                            </Box>
                        }
                    </>
                ) : undefined
            }
        ];

        return elements;
    }, [
        shares,
        currency,
        loading,
        directSalesData
    ]);

    const pointsPromotionElement = useMemo(() => {
        const {
            isPointsPromoActive,
            numOfPoints,
            pointsPromotionName
        } = directSalesData?.promotionInfo || {};

        if (!isPointsPromoActive && !numOfPoints) {
            return null;
        }

        return (
            <>
                <Box my={2}>
                    <Divider />
                </Box>

                <Box
                    color={theme.palette.primary[900]}
                    display="flex"
                    flexDirection="row"
                    justifyContent="space-between"
                    gridGap={isExtraSmallScreen() ? 16 : 0}
                >
                    <Box
                        display="flex"
                        alignItems="center"
                    >
                        <StreamlineIcon className={classes.streamlineIcon}>
                            <Tags/>
                        </StreamlineIcon>

                        <Typography
                            variant="body1"
                            color={"inherit"}
                        >
                            {pointsPromotionName}
                        </Typography>
                    </Box>
                    <Typography
                        variant="body1"
                        color={"inherit"}
                        className={classes.fitContentWidth}
                    >
                        {`+${numOfPoints} ${i18n("common.label.points")}`}
                    </Typography>
                </Box>
            </>
        );
    }, [directSalesData, theme]);

    const loyaltyDiscountElement = useMemo(() => {
        const {
            loyaltyDiscountPercentage,
            numOfShares
        } = directSalesData?.prices || {};

        if (loyaltyDiscountPercentage < 0 && !numOfShares) {
            return null;
        }

        return (
            <Box
                display="flex"
                alignItems="center"
                bgcolor={theme.palette.secondary[100]}
                p={theme.spacing(1, 2, 1, 2)}
                my={2}
            >
                <StreamlineIcon className={classes.lighBulbShineIcon}>
                    <LightBulbShine />
                </StreamlineIcon>

                <Typography
                    variant="body2"
                >
                    {i18n("view.buy_a_share.directly_select_shares_dialog.selection_preview.annual_charge_promotion_hint3", {
                        shares: numOfShares,
                        discount: loyaltyDiscountPercentage
                    })}
                </Typography>
            </Box>
        );
    }, [directSalesData, theme]);

    const getPriceElements = useCallback(async (shares) => {
        setShares(shares);
        if (!directSalesDataLoading && !directSalesDataError) {
            setPricesLoading(true);
            setDirectSalesPricingDataError();

            try {
                const data = await getDirectSalesPricingData(shares);
                setDirectSalesData(data);
                setTotal(data?.prices?.total?.originalPrice);
                setOriginalTotal((data?.prices?.share?.originalPrice + data?.prices?.annualCharge?.originalPrice));
            } catch (err) {
                setDirectSalesPricingDataError(err);
            } finally {
                setPricesLoading(false);
            }
        }
    }, [currency, directSalesDataLoading, directSalesDataError]);

    return {
        getPriceElements,
        priceElements,
        pricesLoading,
        isInitializing: directSalesDataLoading,
        priceTotal: total,
        originalTotal,
        error,
        pointsPromotionElement,
        loyaltyDiscountElement
    };
};

